// src/journey/FlashcardsJourneyView.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import styles from "../views/Flashcards.module.css";
import LoadingSpinner from "../components/LoadingSpinner";
import useJourneyLogic from "../hooks/useJourneyLogic";
import { AVAILABLE_SURAHS } from "../constants/surahs";

function FlashcardsJourneyView({ onComplete, gameLength = 2, config }) {
    const { user } = useAuth();
    const autoAdvanceTimeoutRef = useRef(null);

    // --- State ---
    const [selected, setSelected] = useState(null);
    const [showExplanation, setShowExplanation] = useState(false);
    const [done, setDone] = useState(false);
    const [timer, setTimer] = useState(15);
    const [showInstructions, setShowInstructions] = useState(true);
    const [isCorrect, setIsCorrect] = useState(false);
    const [reviewMode, setReviewMode] = useState(false);

    const ACTIVITY_TYPE = "flashcards";
    const currentSurahId = config?.surah;

    // Get Surah name for display
    const currentSurahName = AVAILABLE_SURAHS.find(s => s.id === currentSurahId)?.name || `Surah ${currentSurahId}`;

    // --- Hook ---
    const {
        questions,
        current,
        setCurrent,
        loadingQuestions,
        error,
        loadQuizQuestions,
        handleCorrectAttempt,
        handleIncorrectAttempt,
    } = useJourneyLogic({
        activityType: ACTIVITY_TYPE,
        gameSpecificEndCondition: done,
        gameLength,
        onComplete,
        currentSurahId,
    });

    // --- Callbacks ---
    const startGame = useCallback(() => {
        setShowInstructions(false);
        setTimer(15);
    }, []);

    const next = useCallback(() => {
        setSelected(null);
        setShowExplanation(false);
        setTimer(15);
        setIsCorrect(false);

        if (current + 1 < questions.length) {
            setCurrent((prev) => prev + 1);
        } else {
            setDone(true);
        }
    }, [current, questions.length, setCurrent]);

    const handleTimeout = useCallback(() => {
        setSelected(null);
        setShowExplanation(true);
        setIsCorrect(false);
        handleIncorrectAttempt(questions[current]?._id);
    }, [handleIncorrectAttempt, questions, current]);

    const handleSelect = useCallback((choiceIndex) => {
        setSelected(choiceIndex);
    }, []);

    const handleSubmit = useCallback(() => {
        if (selected === null) return;
        setShowExplanation(true);

        const currentQ = questions[current];
        const selectedOptionText = currentQ.options[selected];
        const isCorrectAnswer = selectedOptionText === currentQ.correctAnswerText;

        setIsCorrect(isCorrectAnswer);

        if (isCorrectAnswer) {
            handleCorrectAttempt(currentQ?._id);
        } else {
            handleIncorrectAttempt(currentQ?._id);
        }
    }, [selected, current, questions, handleCorrectAttempt, handleIncorrectAttempt]);

    // --- Effects ---
    useEffect(() => {
        if (!user) return;
        loadQuizQuestions(null, false);
    }, [user, loadQuizQuestions]);

    useEffect(() => {
        if (
            showExplanation ||
            done ||
            loadingQuestions ||
            questions.length === 0 ||
            showInstructions
        )
            return;

        const interval = setInterval(() => {
            setTimer((prev) => {
                if (prev === 1) {
                    clearInterval(interval);
                    handleTimeout();
                    return 15;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [current, showExplanation, done, loadingQuestions, questions.length, showInstructions, handleTimeout]);

    useEffect(() => {
        if (!showExplanation) return;
        autoAdvanceTimeoutRef.current = setTimeout(() => {
            next();
        }, 5000); // Increased to 5 seconds for better readability
        return () => clearTimeout(autoAdvanceTimeoutRef.current);
    }, [showExplanation, next]);

    // --- Early exit if not enough questions ---
    if (!loadingQuestions && questions && questions.length < gameLength) {
        return (
            <div className={styles.background} style={{ backgroundImage: `url("/images/flashcards.jpg")` }}>
                <div className={styles.overlay}></div>
                <div className={styles.flashcardDeckWrapper}>
                    <div className={styles.container}>
                        <div className={styles.notEnoughQuestions}>
                            <h2>Not enough questions available for {currentSurahName}.</h2>
                            <p>Please try another activity or surah.</p>
                            <button onClick={() => onComplete(ACTIVITY_TYPE)} className={styles.finishButton}>
                                Return to Journey
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // --- Helpers ---
    const getOptionClass = (choiceIndex) => {
        if (!questions || questions.length === 0 || current >= questions.length) {
            return styles.optionButton;
        }

        const currentQ = questions[current];
        const optionText = currentQ.options[choiceIndex];
        const isSelected = choiceIndex === selected;

        if (showExplanation) {
            const isCorrectOption = optionText === currentQ.correctAnswerText;
            if (isCorrectOption) return `${styles.optionButton} ${styles.correct}`;
            if (isSelected) return `${styles.optionButton} ${styles.incorrect}`;
            return styles.optionButton;
        }

        return isSelected
            ? `${styles.optionButton} ${styles.selected}`
            : styles.optionButton;
    };

    // --- UI ---
    const backgroundStyle = { backgroundImage: `url("/images/flashcards.jpg")` };

    if (!user) {
        return <p className={styles.message}>Please login to access Flashcards Journey Mode.</p>;
    }

    if (loadingQuestions) {
        return (
            <div className={styles.background} style={backgroundStyle}>
                <div className={styles.overlay}></div>
                <div className={styles.flashcardDeckWrapper}>
                    <div className={styles.container}>
                        <LoadingSpinner />
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.background} style={backgroundStyle}>
                <div className={styles.overlay}></div>
                <div className={styles.flashcardDeckWrapper}>
                    <div className={styles.container}>
                        <p className={styles.errorMessage}>Error: {error}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (questions.length === 0 && !loadingQuestions) {
        return (
            <div className={styles.background} style={backgroundStyle}>
                <div className={styles.overlay}></div>
                <div className={styles.flashcardDeckWrapper}>
                    <div className={styles.container}>
                        <p className={styles.errorMessage}>No flashcards available for {currentSurahName}.</p>
                    </div>
                </div>
            </div>
        );
    }

    // --- Review Mode ---
    if (done && reviewMode) {
        return (
            <div className={styles.background} style={backgroundStyle}>
                <div className={styles.overlay}></div>
                <div className={styles.flashcardDeckWrapper}>
                    <div className={`${styles.container} ${styles.reviewContainer}`}>
                        <h2>Review: {currentSurahName}</h2>
                        <div
                            className={styles.reviewContainer}
                            style={{
                                maxHeight: '400px',
                                overflowY: 'auto',
                                padding: '1rem',
                                marginBottom: '1rem'
                            }}
                        >
                            {questions.map((q, idx) => (
                                <div key={idx} className={styles.reviewCard}>
                                    <p className={styles.arabicWordInQuestion}>{q.correctArabicWord}</p>
                                    <p className={styles.transliterationText}>{q.transliteration}</p>
                                    <p className={styles.correct}>
                                        Meaning: {q.correctAnswerText}
                                    </p>
                                </div>
                            ))}
                        </div>
                        <button
                            className={styles.proceedButton}
                            onClick={() => onComplete(ACTIVITY_TYPE)}
                        >
                            Finish Journey Step
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // --- Completion Screen ---
    if (done && !reviewMode) {
        return (
            <div className={`${styles.container} ${done ? styles.completionContainer : ""}`}>
                <div className={styles.overlay}></div>
                <div className={styles.flashcardDeckWrapper}>
                    <div className={styles.container}>
                        <h2>{currentSurahName} Complete!</h2>
                        <p>MashaAllah! You've completed this section.</p>
                        <div style={{display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap'}}>
                            <button
                                className={styles.proceedButton}
                                onClick={() => setReviewMode(true)}
                            >
                                Review Surah
                            </button>
                            <button
                                className={styles.proceedButton}
                                onClick={() => onComplete(ACTIVITY_TYPE)}
                            >
                                Continue Journey
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const currentQ = questions[current];

    // --- Instructions Screen ---
    if (showInstructions) {
        return (
            <div className={styles.background} style={backgroundStyle}>
                <div className={styles.overlay}></div>
                <div className={styles.flashcardDeckWrapper}>
                    <div className={styles.container}>
                        <div className={styles.instructionsPopup}>
                            <h3>Flashcards: {currentSurahName}</h3>
                            <ul>
                                <li>Learn Arabic words from this Surah in sequential order.</li>
                                <li>Choose the correct English meaning for each word.</li>
                                <li>You have 15 seconds to answer each card.</li>
                                <li>Words are presented in the order they appear in the Surah.</li>
                            </ul>
                            <button className={styles.proceedButton} onClick={startGame}>
                                Start
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // --- Main Game Screen ---
    return (
        <div className={styles.background} style={backgroundStyle}>
            <div className={styles.overlay}></div>
            <div className={styles.flashcardDeckWrapper}>
                <div className={styles.container}>
                    <h2 className={styles.title}>{currentSurahName}</h2>
                    <p className={styles.timerText}>Time Left: {timer}s</p>
                    <p className={styles.progressText}>
                        Card {current + 1} of {questions.length}
                    </p>
                    <div className={styles.transliterationQuestion}>
                        <p className={styles.transliterationText}>{currentQ?.transliteration}</p>
                        <p className={styles.arabicWordInQuestion}>{currentQ?.correctArabicWord}</p>
                    </div>
                    <div className={styles.options}>
                        {currentQ?.options.map((opt, idx) => (
                            <button
                                key={idx}
                                onClick={() => handleSelect(idx)}
                                className={getOptionClass(idx)}
                                disabled={showExplanation}
                            >
                                {opt}
                            </button>
                        ))}
                    </div>
                    {!showExplanation && selected !== null && (
                        <button
                            className={styles.proceedButton}
                            onClick={handleSubmit}
                        >
                            Submit
                        </button>
                    )}
                    {showExplanation && (
                        <div
                            style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                backgroundColor: "rgba(0, 0, 0, 0.75)",
                                padding: "2rem",
                                borderRadius: "1rem",
                                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                                width: "90%",
                                maxWidth: "600px",
                                zIndex: 100,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                                gap: "1rem",
                                color: "#eee",
                            }}
                        >
                            <p style={{
                                fontSize: '1.5rem',
                                fontWeight: 'bold',
                                color: isCorrect ? '#4CAF50' : '#f44336',
                                marginBottom: '1rem',
                            }}>
                                {isCorrect ? "✅ Correct, MashaAllah!" : "❌ Incorrect"}
                            </p>
                            <p style={{ fontSize: '1.2rem' }}>
                                Correct Meaning: <strong style={{ color: '#A5D6A7' }}>{currentQ.correctAnswerText}</strong>
                            </p>
                            <p style={{ fontSize: '1.1rem', color: '#ddd' }}>
                                Arabic Word: <strong>{currentQ.correctArabicWord}</strong>
                            </p>
                            <p style={{
                                fontSize: '0.9rem',
                                fontStyle: 'italic',
                                color: '#bbb',
                                marginTop: '1rem',
                            }}>
                                Moving to next card automatically...
                            </p>
                            {/* ✅ Next button */}
                            <button
                                className={styles.proceedButton}
                                onClick={next}
                                style={{ marginTop: "1rem" }}
                            >
                                Next
                            </button>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}

export default FlashcardsJourneyView;